import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, TextField
} from '@mui/material';
import { getLogEvents } from '../../utils/clientUtils';
import { useTranslation } from 'react-i18next';

function parseLogMessage(raw, t) {
    const parts = raw.trim().split(' ');
    const action = parts[0];
    const params = {};
    for (let i = 1; i < parts.length; i++) {
        const idx = parts[i].indexOf('=');
        if (idx !== -1) {
            params[parts[i].slice(0, idx)] = parts[i].slice(idx + 1);
        }
    }
    const on = v => v === 'True' || v === 'true';
    const user = params.user === 'scheduler' ? t('deviceLogs.scheduler') : params.user;
    switch (action) {
        case 'connected':    return t('deviceLogs.connected');
        case 'disconnected': return t('deviceLogs.disconnected');
        case 'set_board':
            return t('deviceLogs.setBoard', { user, board: params.board, image: params.image });
        case 'set_display':
            return t('deviceLogs.setDisplay', { user, state: on(params.value) ? t('deviceLogs.on') : t('deviceLogs.off') });
        case 'set_light':
            return t('deviceLogs.setLight', { user, light: params.light, state: on(params.value) ? t('deviceLogs.on') : t('deviceLogs.off') });
        case 'command':
            return t('deviceLogs.sentCommand', { user, data: params.data });
        case 'scheduled_event': {
            const isOn = v => v === 'on' || v === 'true' || v === 'True';
            const changes = [];
            if ('display' in params) changes.push(`${t('deviceControls.display')}: ${isOn(params.display) ? t('deviceLogs.on') : t('deviceLogs.off')}`);
            if ('light1' in params) changes.push(`${t('deviceControls.light1')}: ${isOn(params.light1) ? t('deviceLogs.on') : t('deviceLogs.off')}`);
            if ('light2' in params) changes.push(`${t('deviceControls.light2')}: ${isOn(params.light2) ? t('deviceLogs.on') : t('deviceLogs.off')}`);
            Object.entries(params)
                .filter(([k]) => k.startsWith('image_'))
                .sort()
                .forEach(([k, v]) => {
                    const boardN = parseInt(k.slice(6), 10) + 1;
                    changes.push(`${t('deviceSchedule.board', { n: boardN })}: ${v}`);
                });
            return changes.length ? `${user}: ${changes.join(', ')}` : user;
        }
        default:
            return raw;
    }
}

function dateToStartMs(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr + 'T00:00:00').getTime();
}

function dateToEndMs(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr + 'T23:59:59.999').getTime();
}

function DeviceLogs() {
    const { t } = useTranslation();
    const { deviceId } = useParams();
    const [events, setEvents] = useState([]);
    const [nextToken, setNextToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const loadInitial = useCallback(async () => {
        if (!deviceId) return;
        setLoading(true);
        setEvents([]);
        setNextToken(null);
        setHasMore(false);
        const result = await getLogEvents(deviceId, null, dateToStartMs(fromDate), dateToEndMs(toDate));
        setEvents(result.events ?? []);
        const token = result.nextToken ?? null;
        setNextToken(token);
        setHasMore(!!token);
        setLoading(false);
    }, [deviceId, fromDate, toDate]);

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    const loadMore = async () => {
        if (!nextToken) return;
        setLoadingMore(true);
        const result = await getLogEvents(deviceId, nextToken);
        const older = result.events ?? [];
        const token = result.nextToken ?? null;
        setEvents(prev => [...prev, ...older]);
        setHasMore(!!token && token !== nextToken);
        setNextToken(token);
        setLoadingMore(false);
    };

    if (!deviceId) {
        return (
            <Box sx={{ p: 2, color: 'text.secondary' }}>
                <Typography>{t('deviceLogs.selectDevice')}</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6">{t('deviceLogs.device', { id: deviceId })}</Typography>
                <TextField
                    label={t('deviceLogs.from')}
                    type="date"
                    size="small"
                    value={fromDate}
                    onChange={e => setFromDate(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { lang: 'sv-SE' } }}
                />
                <TextField
                    label={t('deviceLogs.to')}
                    type="date"
                    size="small"
                    value={toDate}
                    onChange={e => setToDate(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { lang: 'sv-SE' } }}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : events.length === 0 ? (
                <Typography color="text.secondary">{t('deviceLogs.noLogs')}</Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ whiteSpace: 'nowrap', width: 200 }}>{t('deviceLogs.time')}</TableCell>
                                <TableCell>{t('deviceLogs.message')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events.map((e, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
                                        {new Date(e.timestamp).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' })}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem' }}>{parseLogMessage(e.message, t)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {hasMore && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={loadMore}
                        disabled={loadingMore}
                    >
                        {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                        {t('deviceLogs.loadOlder')}
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export default DeviceLogs;
